'use client';

import React from 'react';
import Image from 'next/image';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getCapitaalePartnerUrl } from '@/lib/capitaalePartnerUrl';

export default function CapitaalePartnerSection() {
  const { t, lang } = useLanguage();
  const partnerUrl = getCapitaalePartnerUrl(lang);

  return (
    <section aria-label={t('capitaale.aria')} className="pb-20 lg:pb-28 bg-white -mt-8 lg:-mt-12">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <article className="relative overflow-hidden rounded-2xl border border-slate-800/80 bg-[#0a0f14] p-6 sm:p-8 lg:p-10">
          <div
            className="pointer-events-none absolute inset-0 opacity-60"
            aria-hidden
            style={{
              background:
                'radial-gradient(ellipse 80% 120% at 0% 50%, rgba(34, 211, 238, 0.12), transparent 55%), radial-gradient(ellipse 70% 100% at 100% 0%, rgba(52, 211, 153, 0.1), transparent 50%), radial-gradient(ellipse 50% 80% at 85% 100%, rgba(234, 179, 8, 0.08), transparent 45%)',
            }}
          />
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
            <div className="shrink-0 flex items-center justify-center lg:justify-start">
              <Image
                src="/images/capitaale-logo.png"
                alt="Capitaale"
                width={220}
                height={48}
                className="h-10 sm:h-11 w-auto"
                priority={false}
              />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-cyan-400/90 mb-2">
                {t('capitaale.tag')}
              </p>
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed">{t('capitaale.desc')}</p>
            </div>

            <div className="shrink-0 lg:self-center">
              <a
                href={partnerUrl}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold text-[#0a0f14] bg-gradient-to-r from-cyan-300 via-emerald-300 to-amber-200 hover:from-cyan-200 hover:via-emerald-200 hover:to-amber-100 transition-all duration-150 whitespace-nowrap"
              >
                {t('capitaale.cta')}
                <ArrowRight size={16} className="shrink-0" aria-hidden />
              </a>
              <p className="mt-2 text-[10px] text-slate-500 text-center lg:text-right flex items-center justify-center lg:justify-end gap-1">
                <ExternalLink size={10} aria-hidden />
                {t('capitaale.external')}
              </p>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
